import Flex from "@/components/Flex";
import ChipSelector from "@/components/ChipSelector";
import { UseScrollSpy, useScrollSpy } from "@/hooks/use-scroll-spy";
import { FormikProps } from "formik";
import { ReactNode, RefObject, useMemo } from "react";
import {
  GROUP_EDIT_SECTIONS,
  GROUP_SECTION,
  GROUP_SECTION_NAV,
} from "../group-visit-constant";
import { GroupFormValues } from "../group-visit-types";
import GroupAvailabilitySection from "./GroupAvailabilitySection";
import GroupInformationSection from "./GroupInformationSection";
import ProviderAssignmentSection from "./ProviderAssignmentSection";
import SessionConfigurationSection from "./SessionConfigurationSection";

/** Quick-jump chips for the sections on screen, in order. */
export const getGroupSectionNav = (sections: string[]) =>
  GROUP_SECTION_NAV.filter((item) => sections.includes(item.id));

export type GroupFormProps = {
  formik: FormikProps<GroupFormValues>;
  /** Sections to render, in order. Defaults to every section. */
  sections?: string[];
  /** Read-only rendering, e.g. for an archived group or a view-only user. */
  disabled?: boolean;
  showAmount?: boolean;
  /** Defaults to showing the quick-jump chips only for a multi-section form. */
  showSectionNav?: boolean;
  /** Scroll container the section chips jump within; defaults to the viewport. */
  scrollContainerRef?: RefObject<HTMLElement>;
  /**
   * Supply to host the section chips outside the form — the form then renders
   * none and only registers its sections with the given spy.
   */
  scrollSpy?: UseScrollSpy;
};

/**
 * @author Rajkumar Rathod
 */
const GroupForm = ({
  formik,
  sections = GROUP_EDIT_SECTIONS,
  disabled,
  showAmount = true,
  showSectionNav,
  scrollContainerRef,
  scrollSpy,
}: GroupFormProps) => {
  const ownScrollSpy = useScrollSpy({
    sectionIds: sections,
    containerRef: scrollContainerRef,
  });

  const { activeId, scrollTo, registerSection } = scrollSpy ?? ownScrollSpy;

  const navItems = useMemo(() => getGroupSectionNav(sections), [sections]);

  /** A one-section form is the whole drawer — the titled card adds no grouping. */
  const isSingleSection = sections.length === 1;

  const sectionNodes: Record<string, ReactNode> = {
    [GROUP_SECTION.INFORMATION]: (
      <GroupInformationSection
        ref={registerSection(GROUP_SECTION.INFORMATION)}
        formik={formik}
        disabled={disabled}
        showAmount={showAmount}
        hideSectionCard={isSingleSection}
      />
    ),
    [GROUP_SECTION.SESSION]: (
      <SessionConfigurationSection
        ref={registerSection(GROUP_SECTION.SESSION)}
        formik={formik}
        disabled={disabled}
      />
    ),
    [GROUP_SECTION.PROVIDER]: (
      <ProviderAssignmentSection
        ref={registerSection(GROUP_SECTION.PROVIDER)}
        formik={formik}
        disabled={disabled}
      />
    ),
    [GROUP_SECTION.AVAILABILITY]: (
      <GroupAvailabilitySection
        ref={registerSection(GROUP_SECTION.AVAILABILITY)}
        formik={formik}
        disabled={disabled}
      />
    ),
  };

  const isNavVisible = showSectionNav ?? (!scrollSpy && sections.length > 1);

  return (
    <Flex flexDirection="column" gap={2}>
      {isNavVisible && (
        <ChipSelector
          items={navItems}
          activeId={activeId}
          onSelect={scrollTo}
        />
      )}

      {sections.map((section) => (
        <Flex key={section} flexDirection="column">
          {sectionNodes[section]}
        </Flex>
      ))}
    </Flex>
  );
};

export default GroupForm;
